import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import SafeIcon from '../../common/SafeIcon';
import Button from '../../components/common/Button';
import * as FiIcons from 'react-icons/fi';

const { 
  FiPlus, FiSearch, FiFilter, FiGrid, FiList, FiMoreVertical, 
  FiEdit, FiTrash2, FiShare2, FiEye, FiLock, FiUnlock, FiHeart,
  FiDownload, FiUpload, FiFolder, FiImage, FiTag, FiCalendar
} = FiIcons;

const GalleryManager = () => {
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const galleries = [
    {
      id: 1,
      title: 'Summer Wedding Collection',
      description: 'Beautiful outdoor wedding ceremony and reception',
      coverImage: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=400&h=300&fit=crop',
      photoCount: 247,
      views: 1230,
      likes: 89,
      privacy: 'public',
      createdAt: '2024-01-10',
      tags: ['wedding', 'outdoor', 'summer'],
      clientEmail: 'sarah@example.com'
    },
    {
      id: 2,
      title: 'Corporate Headshots',
      description: 'Professional headshots for tech company',
      coverImage: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=300&fit=crop',
      photoCount: 45,
      views: 567,
      likes: 23,
      privacy: 'private',
      createdAt: '2024-01-08',
      tags: ['corporate', 'headshots', 'professional'],
      clientEmail: 'hr@techcorp.com'
    },
    {
      id: 3,
      title: 'Family Portrait Session',
      description: 'Annual family photos at the park',
      coverImage: 'https://images.unsplash.com/photo-1511895426328-dc8714191300?w=400&h=300&fit=crop',
      photoCount: 78,
      views: 456,
      likes: 34,
      privacy: 'shared',
      createdAt: '2024-01-05',
      tags: ['family', 'portrait', 'outdoor'],
      clientEmail: 'johnson.family@email.com'
    },
    {
      id: 4,
      title: 'Product Photography',
      description: 'E-commerce product shots for online store',
      coverImage: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop',
      photoCount: 156,
      views: 892,
      likes: 67,
      privacy: 'public',
      createdAt: '2024-01-03',
      tags: ['product', 'ecommerce', 'studio'],
      clientEmail: 'store@fashion.com'
    }
  ];

  const getPrivacyIcon = (privacy) => {
    switch (privacy) {
      case 'public': return FiUnlock;
      case 'private': return FiLock;
      case 'shared': return FiShare2;
      default: return FiLock;
    }
  };

  const getPrivacyColor = (privacy) => {
    switch (privacy) {
      case 'public': return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400';
      case 'private': return 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400';
      case 'shared': return 'text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  const filteredGalleries = galleries.filter(gallery =>
    gallery.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    gallery.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Gallery Manager
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Organize and manage your photo collections
            </p>
          </div>
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            icon={FiPlus}
            className="mt-4 sm:mt-0"
          >
            Create Gallery
          </Button>
        </div>

        {/* Toolbar */}
        <div className="bg-white dark:bg-dark-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search galleries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* View Controls */}
            <div className="flex items-center space-x-2">
              <Button
                variant={viewMode === 'grid' ? 'primary' : 'ghost'}
                size="sm"
                icon={FiGrid}
                onClick={() => setViewMode('grid')}
              />
              <Button
                variant={viewMode === 'list' ? 'primary' : 'ghost'}
                size="sm"
                icon={FiList}
                onClick={() => setViewMode('list')}
              />
              <Button variant="ghost" size="sm" icon={FiFilter}>
                Filter
              </Button>
            </div>
          </div>
        </div>

        {/* Gallery Grid/List */}
        <motion.div
          layout
          className={`${
            viewMode === 'grid'
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
              : 'space-y-4'
          }`}
        >
          {filteredGalleries.map((gallery) => (
            <motion.div
              key={gallery.id}
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`bg-white dark:bg-dark-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow ${
                viewMode === 'list' ? 'flex items-center' : ''
              }`}
            >
              {/* Cover Image */}
              <div className={`relative ${viewMode === 'list' ? 'w-32 h-24 flex-shrink-0' : 'aspect-video'}`}>
                <img
                  src={gallery.coverImage}
                  alt={gallery.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-20 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center space-x-2">
                  <Button variant="ghost" size="sm" className="text-white hover:bg-white/20">
                    <SafeIcon icon={FiEye} className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-white hover:bg-white/20">
                    <SafeIcon icon={FiEdit} className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-white hover:bg-white/20">
                    <SafeIcon icon={FiShare2} className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* Privacy Badge */}
                <div className="absolute top-2 left-2">
                  <div className={`px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${getPrivacyColor(gallery.privacy)}`}>
                    <SafeIcon icon={getPrivacyIcon(gallery.privacy)} className="w-3 h-3" />
                    <span className="capitalize">{gallery.privacy}</span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className={`p-4 ${viewMode === 'list' ? 'flex-1' : ''}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                      {gallery.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                      {gallery.description}
                    </p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1 mb-3">
                      {gallery.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded-full"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>

                    {/* Stats */}
                    <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiImage} className="w-4 h-4" />
                        <span>{gallery.photoCount}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiEye} className="w-4 h-4" />
                        <span>{gallery.views}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiHeart} className="w-4 h-4" />
                        <span>{gallery.likes}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                        <span>{new Date(gallery.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>

                  {/* Actions Menu */}
                  <div className="relative">
                    <Button variant="ghost" size="sm">
                      <SafeIcon icon={FiMoreVertical} className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Empty State */}
        {filteredGalleries.length === 0 && (
          <div className="text-center py-16">
            <SafeIcon icon={FiFolder} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No galleries found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {searchTerm ? 'Try adjusting your search terms' : 'Create your first gallery to get started'}
            </p>
            <Button onClick={() => setIsCreateModalOpen(true)} icon={FiPlus}>
              Create Gallery
            </Button>
          </div>
        )}
      </div>

      {/* Create Gallery Modal */}
      <AnimatePresence>
        {isCreateModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setIsCreateModalOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white dark:bg-dark-800 rounded-lg p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                Create New Gallery
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Gallery Title
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-white"
                    placeholder="Enter gallery title"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-white"
                    placeholder="Describe your gallery"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Privacy
                  </label>
                  <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-white">
                    <option value="private">Private</option>
                    <option value="shared">Shared with Link</option>
                    <option value="public">Public</option>
                  </select>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <Button
                  variant="ghost"
                  onClick={() => setIsCreateModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button onClick={() => setIsCreateModalOpen(false)}>
                  Create Gallery
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </DashboardLayout>
  );
};

export default GalleryManager;